import { AvatarCustom, GvPrimaryButton } from "../../../components";
import { MODALS, UPSELL_RENEWAL_ENTRY_POINT_KEY, WELLNESS_RENEWAL_ENTRY_POINTS } from "../../../config/params";
import React, { useEffect, useState } from "react";

import { Box } from "@mui/material";
import { CloseIcon } from "../../../components/Icons";
import { IPetItemResponse } from "../../../services/pet-list/pet-list.interface";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import { isWellnessMember } from "../../../utils/wellness";
import { useModal } from "../../../context/ModalContext";
import { useNavigate } from "react-router-dom";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { useUserStore } from "../../../hooks/redux-hooks/user-store/user.store";
import { userActivityLogger } from "../../../utils/mixpanel";

const WellnessPlanSelectPetContainer = () => {
  const { petList } = usePetStore();
  const [selectedPet, setSelectedPet] = useState<IPetItemResponse>();
  const [wellnessPlanPetList, setWellnessPLanPetList]=useState<IPetItemResponse[]>();
  const { user } = useUserStore();
  const { closeModal, setWellnessSelectedPetId, openModal, params } = useModal();
  const navigate = useNavigate();
  const filterPetIds = params?.petIds as string[] | undefined;

  const handleSelectPet = (pet: IPetItemResponse) => {
    userActivityLogger(MIXPANEL_EVENTS.USER_WP_PET_SELECTION, { pet_id: pet.id, pet_name: pet.name });
    setSelectedPet(pet);
  };

  useEffect(()=>{
    const wellnessRedirect = JSON.parse(localStorage.getItem("wellness-utm") || "{}");

    if (wellnessRedirect.isWellnessPageRedirect) {
      localStorage.setItem("wellness-utm", JSON.stringify({ ...wellnessRedirect, isWellnessPageRedirect: false }));
    }
  }, []);


  const handlePetSelection = () => {
    if (selectedPet && petList) {
      setWellnessSelectedPetId(selectedPet.id);
      const isNotAVaildBirthdate =
        selectedPet.birthdate.length === 0 ||
        parseInt(selectedPet.birthdate) <= 0 ||
        new Date(parseInt(selectedPet.birthdate) * 1000).getFullYear() < 2000;

      // If filterPetIds provided (renewal flow), skip wellness member check
      if (filterPetIds && filterPetIds.length > 0) {
        // Renewal flow - always go to plan comparison after birthday check
        localStorage.setItem(
          UPSELL_RENEWAL_ENTRY_POINT_KEY,
          WELLNESS_RENEWAL_ENTRY_POINTS.WELLNESS_ALL_PLANS,
        );
        if (isNotAVaildBirthdate) {
          openModal(MODALS.PET_BIRTHDAY_FORM_MODAL);
        } else {
          navigate(`${PAGES_PATH.WELLNESS_PLAN_DETAILS}?id=${selectedPet.id}`);
        }
      } else {
        // General wellness flow - check if pet already has a plan
        isWellnessMember(petList, selectedPet.id, true)?
          navigate(`${PAGES_PATH.PET_CARE}?id=${selectedPet.id}&category=TotalCare_Plan`):
          (isNotAVaildBirthdate)?
            openModal(MODALS.PET_BIRTHDAY_FORM_MODAL):
            navigate(`${PAGES_PATH.WELLNESS_PLAN_DETAILS}?id=${selectedPet.id}`);
      }
    }
  };

  useEffect(() => {
    userActivityLogger(MIXPANEL_EVENTS.WELLNESS_PLAN_SELECT_PET_CONTAINER_SHOWN, {
      pet_id: selectedPet?.id,
      pet_name: selectedPet?.name,
    });
  }, []);

  useEffect(() => {
    if (petList) {
      const pets = petList
        .filter((data: IPetItemResponse) => {
          return (data.speciesName === "Dog" || data.speciesName === "Cat");
        })
        .filter((pet: IPetItemResponse) => {
          // If petIds provided in params, filter to only those specific pets
          if (filterPetIds && filterPetIds.length > 0) {
            return filterPetIds.includes(pet.id);
          }

          // Only filter out wellness members if no specific petIds provided
          return !pet.isWellnessplan;
        });

      setWellnessPLanPetList(pets);
    }
  }, [petList, filterPetIds]);

  return (
    <Box className="h-full flex flex-col justify-between p-2">
      <Box
        className="flex justify-end cursor-pointer w-full pb-2"
        onClick={closeModal}
      >
        <span className="bg-gray-6 p-0.5 rounded-full">
          <CloseIcon />
        </span>
      </Box>
      <Box className="w-full mb-2">
        <h1 className="text-3xl leading-8 grenette-pro mx-18 text-center my-2 font-semibold">
          TotalCare Plans That Grow<br/> With Your Pet
        </h1>
        <p className="text-center">Select a pet you&apos;d like to purchase a TotalCare plan for.<br/>
           Don&apos;t worry—you can add plans for other pets later.</p>
      </Box>
      <Box className="grow shrink mb-1 overflow-y-scroll px-1 flex flex-col gap-4">
        {wellnessPlanPetList &&
        wellnessPlanPetList.length > 0 ?
          wellnessPlanPetList?.map((pet: IPetItemResponse) => (
            <div className={`border-2  rounded-lg p-4 cursor-pointer hover:border-violet-1
              ${selectedPet?.id === pet.id ? "border-violet-1 bg-violet-3" : "border-taupe-50 bg-white"}`}
            onClick={() => handleSelectPet(pet)}
            key={pet.id}
            >
              <div className="flex gap-3 w-full items-center">
                <div>
                  <AvatarCustom
                    width={70}
                    height={70}
                    image={pet.image}
                    name={pet.name}
                    isWellnessMember={Boolean(pet.isWellnessplan)}
                  />
                </div>
                <div className="space-y-1">
                  <div className="font-semibold capitalize">
                    <p>
                      {pet.name}
                    </p>
                  </div>
                  <div className="text-sm flex gap-1">
                    <span className="">Eligible at</span>
                    <span className="font-semibold">GoodVets {user?.location?.locationName}</span>
                  </div>
                </div>
              </div>
            </div>
          )) :
          <div className="flex flex-col items-center justify-center text-gray-1 h-72">
            <div>Wellness plans are only available for dogs and cats.</div>
            <div>We&apos;re continually expanding our services.</div>
            <div>Check back for updates!</div>
          </div>
        }
      </Box>
      <Box className="!sticky !bottom-0 pt-1">
        <GvPrimaryButton type="submit"
          className="!font-bold" isDisabled={!selectedPet} onClick={handlePetSelection} >
                  Continue
        </GvPrimaryButton>
      </Box>
    </Box>
  );
};

export default WellnessPlanSelectPetContainer;
